import React, { useMemo, useRef, useState } from 'react';
import { Alert, Collapse } from 'antd';
import type { ExecutionDashboardProjection } from '../../routes/execution/dashboardProjection';
import type { ExecutionCargoHydration, ExecutionProgressHydration } from '../types';
import type { ExecutionActionSubmitPayload } from '../ExecutionActionModal';
import type { ExecutionIncident, ExecutionIncidentType } from '../../routes/execution/incidentFacade';
import type { HumanizedCargoMeta } from '../../routes/execution/cargoHumanization';
import { buildDispatchOrchestration } from './dispatchOrchestration';
import {
  buildDestinationAwareness,
  buildSharedUnloadHints,
  buildStopQuickActionMap,
  type SharedUnloadHint
} from './dispatcherContextProjection';
import { completionWarningsToStripItems } from '../../operationalDomain/completion/completionReadiness';
import ExecutionStatusStrip from './ExecutionStatusStrip';
import DispatchOrchestrationBanner from './DispatchOrchestrationBanner';
import DestinationAwarenessPanel from './DestinationAwarenessPanel';
import FinanceCompactStrip from './FinanceCompactStrip';
import ExecutionExpensePanel from './ExecutionExpensePanel';
import ExecutionProcessPanel from './ExecutionProcessPanel';
import ExecutionWorkloadStrip from './ExecutionWorkloadStrip';
import ExecutionRecommendationStrip from './ExecutionRecommendationStrip';
import ExecutionComplianceStrip from './ExecutionComplianceStrip';
import type { RouteOperationalProcessesPayload } from '../../operationalDomain/processes/operationalProcessTypes';
import type { RouteExpense } from '../../../store/api/financeApi';
import DispatchAttentionPanel from './DispatchAttentionPanel';
import CompletionReadinessStrip from './CompletionReadinessStrip';
import ActiveOperationPanel from './ActiveOperationPanel';
import VehicleCargoStrip from './VehicleCargoStrip';
import RouteFlowCompact from './RouteFlowCompact';
import IncidentsCompact from './IncidentsCompact';
import IncidentResolveModal from './IncidentResolveModal';
import CompactEventFeed from './CompactEventFeed';
import { OperationalRouteCockpit, buildOperationalCockpit } from '../../operationalCockpit';
import { isOperationalCockpitMode } from '../../routes/execution/executionProjectionConsumerFlags';
import { CoordinationWorkspaceHint } from '../../operationalCoordination';
import type { CoordinationFleetContext } from '../../operationalCoordination/types';
import { buildCargoHumanizationIndex } from '../../routes/execution/cargoHumanization';
import DispatchActionSurface from './DispatchActionSurface';
import './executionWorkspace.css';

export type ExecutionDispatcherWorkspaceProps = {
  dashboard: ExecutionDashboardProjection;
  routeId: number;
  hydration: ExecutionProgressHydration;
  cargoAtStop: ExecutionCargoHydration[];
  openDocumentTasks?: number;
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  onModeChange?: () => void;
  onResolveIncident?: (incidentId: number, resolutionComment: string) => Promise<void>;
  onOpenIncidentReport?: (preset?: ExecutionIncidentType) => void;
  incidentReportRequest?: ExecutionIncidentType | null;
  onClearIncidentRequest?: () => void;
  resolvingIncidentId?: number | null;
  actionLoading?: boolean;
  routeExpenses?: RouteExpense[];
  expensesLoading?: boolean;
  operationalProcesses?: RouteOperationalProcessesPayload | null;
  processesLoading?: boolean;
  technicalDetails?: React.ReactNode;
  cargoIndex?: Map<number, HumanizedCargoMeta>;
  incidents?: ExecutionIncident[];
  fleetContext?: CoordinationFleetContext;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
};

export const ExecutionDispatcherWorkspace: React.FC<ExecutionDispatcherWorkspaceProps> = ({
  dashboard,
  routeId,
  hydration,
  cargoAtStop,
  openDocumentTasks = 0,
  onSubmit,
  onOpenCompletion,
  onModeChange,
  onResolveIncident,
  onOpenIncidentReport,
  incidentReportRequest = null,
  onClearIncidentRequest,
  resolvingIncidentId = null,
  actionLoading = false,
  routeExpenses = [],
  expensesLoading = false,
  operationalProcesses = null,
  processesLoading = false,
  technicalDetails,
  cargoIndex: cargoIndexProp,
  incidents: incidentsProp = [],
  fleetContext,
  plannedLoadingDate,
  plannedUnloadingDate
}) => {
  const incidentsRef = useRef<HTMLDivElement>(null);
  const [resolveModalIncidentId, setResolveModalIncidentId] = useState<number | null>(null);

  const orchestration = useMemo(
    () => buildDispatchOrchestration(dashboard, { openDocumentTasks }),
    [dashboard, openDocumentTasks]
  );

  const destination = useMemo(() => buildDestinationAwareness(dashboard), [dashboard]);
  const sharedUnloadHints = useMemo(() => {
    const raw = buildSharedUnloadHints(hydration);
    const enriched = new Map<string, SharedUnloadHint>();
    raw.forEach((hint, uid) => {
      const flowStop = dashboard.routeFlow.stops.find((s) => s.executionStopUid === uid);
      enriched.set(uid, {
        ...hint,
        destinationLabel: flowStop?.destinationLabel ?? hint.destinationLabel
      });
    });
    return enriched;
  }, [hydration, dashboard.routeFlow.stops]);
  const stopQuickActions = useMemo(
    () => buildStopQuickActionMap(dashboard.actions),
    [dashboard.actions]
  );

  const cockpitMode = isOperationalCockpitMode();
  const cargoIndex = useMemo(
    () => cargoIndexProp ?? buildCargoHumanizationIndex([], []),
    [cargoIndexProp]
  );

  const processAttentions = useMemo(
    () => operationalProcesses?.attentions ?? [],
    [operationalProcesses?.attentions]
  );

  const cockpitModel = useMemo(
    () =>
      cockpitMode
        ? buildOperationalCockpit({
            hydration,
            cargoIndex,
            incidents: incidentsProp,
            processAttentions,
            activeStopUid: dashboard.activeOperation?.executionStopUid ?? null,
            fleetContext,
            plannedLoadingDate,
            plannedUnloadingDate
          })
        : null,
    [
      cockpitMode,
      hydration,
      cargoIndex,
      incidentsProp,
      processAttentions,
      dashboard.activeOperation?.executionStopUid,
      fleetContext,
      plannedLoadingDate,
      plannedUnloadingDate
    ]
  );

  const completionReadiness = dashboard.completionReadiness;

  const completionItems = useMemo(() => {
    const cr = completionReadiness;
    if (!cr.showHints) return [];
    const items = completionWarningsToStripItems({
      canComplete: cr.canComplete,
      blockers: [],
      warnings: cr.warnings,
      readinessState: cr.readinessState
    });
    if (routeExpenses.length > 0) {
      return items.filter((i) => i.id !== 'expenses_unconfirmed');
    }
    return items;
  }, [completionReadiness, routeExpenses.length]);

  const resolveIncidentLabel = useMemo(() => {
    if (resolveModalIncidentId == null) return undefined;
    return dashboard.incidents.items.find((i) => i.id === resolveModalIncidentId)?.typeLabel;
  }, [resolveModalIncidentId, dashboard.incidents.items]);

  const scrollToIncidents = () => {
    incidentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const requestResolve = (incidentId: number) => {
    setResolveModalIncidentId(incidentId);
  };

  const handleConfirmResolve = async (incidentId: number, resolutionComment: string) => {
    if (!onResolveIncident) return;
    await onResolveIncident(incidentId, resolutionComment);
    setResolveModalIncidentId(null);
  };

  const actionSurfaceProps = {
    routeId,
    orchestration,
    cargoAtStop,
    onSubmit,
    onOpenCompletion,
    onResolveIncident: onResolveIncident ? requestResolve : undefined,
    onModeChange,
    loading: actionLoading,
    incidentReportRequest,
    onClearIncidentRequest
  };

  const showLegacyActionPanel = !cockpitMode || !cockpitModel;

  return (
    <div className="execution-workspace">
      {cockpitMode ? (
        <OperationalRouteCockpit
          model={cockpitModel}
          hydration={hydration}
          cargoIndex={cargoIndex}
          incidents={incidentsProp}
          processAttentions={processAttentions}
          activeStopUid={dashboard.activeOperation?.executionStopUid ?? null}
          actionsSlot={cockpitModel ? <DispatchActionSurface {...actionSurfaceProps} /> : undefined}
        />
      ) : null}
      <ExecutionStatusStrip
        dashboard={dashboard}
        composition={orchestration.composition}
        intelligence={cockpitModel?.intelligence ?? null}
        coordination={cockpitModel?.coordination ?? null}
        recovery={cockpitModel?.recovery ?? null}
      />
      <CoordinationWorkspaceHint coordination={cockpitModel?.coordination} />
      {dashboard.completionLock.locked ? (
        <Alert
          type="success"
          showIcon
          message="Маршрут завершён"
          description={
            dashboard.completionLock.completedAt
              ? `Зафиксировано ${new Date(dashboard.completionLock.completedAt).toLocaleString('ru-RU')}`
              : 'Операционное завершение зафиксировано'
          }
          style={{ marginBottom: 8 }}
        />
      ) : null}
      {!cockpitMode ? <DestinationAwarenessPanel destination={destination} /> : null}
      <FinanceCompactStrip finance={dashboard.financeReadiness} />
      <ExecutionExpensePanel expenses={routeExpenses} loading={expensesLoading} />
      <ExecutionWorkloadStrip payload={operationalProcesses ?? undefined} />
      <ExecutionRecommendationStrip payload={operationalProcesses ?? undefined} />
      <ExecutionComplianceStrip payload={operationalProcesses ?? undefined} />
      <ExecutionProcessPanel payload={operationalProcesses} loading={processesLoading} />
      <DispatchOrchestrationBanner orchestration={orchestration} />
      <DispatchAttentionPanel orchestration={orchestration} onFocusIncident={() => scrollToIncidents()} />
      <CompletionReadinessStrip
        items={completionItems}
        onOpenCompletion={dashboard.completionLock.canEdit ? onOpenCompletion : undefined}
      />
      {showLegacyActionPanel ? (
        <ActiveOperationPanel
          routeId={routeId}
          activeOperation={dashboard.activeOperation}
          orchestration={orchestration}
          cargoAtStop={cargoAtStop}
          onSubmit={onSubmit}
          onOpenCompletion={onOpenCompletion}
          onResolveIncident={onResolveIncident ? requestResolve : undefined}
          onModeChange={onModeChange}
          actionLoading={actionLoading}
          incidentReportRequest={incidentReportRequest}
          onClearIncidentRequest={onClearIncidentRequest}
        />
      ) : null}
      {!cockpitMode ? <VehicleCargoStrip vehicleCargo={dashboard.vehicleCargo} /> : null}
      <RouteFlowCompact
        routeId={routeId}
        routeFlow={dashboard.routeFlow}
        composition={orchestration.composition}
        sharedUnloadHints={sharedUnloadHints}
        stopQuickActions={stopQuickActions}
        hydrationCargo={hydration.cargo}
        onSubmit={onSubmit}
        actionLoading={actionLoading}
      />
      <div ref={incidentsRef}>
        <IncidentsCompact
          incidents={dashboard.incidents}
          onResolve={onResolveIncident ? requestResolve : undefined}
          onOpenIncidentReport={onOpenIncidentReport}
          resolvingId={resolvingIncidentId}
        />
      </div>
      {!cockpitMode ? <CompactEventFeed timeline={dashboard.timeline} /> : null}
      <IncidentResolveModal
        open={resolveModalIncidentId != null}
        incidentId={resolveModalIncidentId}
        incidentLabel={resolveIncidentLabel}
        loading={resolvingIncidentId != null}
        onCancel={() => setResolveModalIncidentId(null)}
        onConfirm={handleConfirmResolve}
      />
      {technicalDetails ? (
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'technical',
              label: <span className="execution-workspace__muted">Технические детали</span>,
              children: technicalDetails
            }
          ]}
        />
      ) : null}
    </div>
  );
};

export default ExecutionDispatcherWorkspace;
